import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Section, Heading } from "../components/Bits";
import PageHero from "../components/PageHero";
import LoginGate from "../components/LoginGate";
import { Send, Sparkles } from "lucide-react";

export default function Coach() {
  const { user } = useAuth() || {};
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Moien! Ich bin dein KI-Naturcoach. Frag mich alles rund um Wildpflanzen in Luxemburg – Erkennung, Verwendung, Rezepte, Heilwirkung. 🌿" },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    if (!input.trim()) return;
    const userMsg = { role: "user", content: input };
    setMessages((m) => [...m, userMsg]);
    setInput("");
    setLoading(true);
    try {
      const r = await api.post("/coach/chat", { message: userMsg.content, session_id: sessionId });
      setSessionId(r.data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: r.data.answer }]);
    } catch (e) {
      toast.error("Coach nicht erreichbar");
      setMessages((m) => [...m, { role: "assistant", content: "Entschuldige, ich konnte gerade nicht antworten. Bitte versuche es erneut." }]);
    } finally {
      setLoading(false);
    }
  };

  const suggestions = [
    "Wie unterscheide ich Bärlauch von Maiglöckchen?",
    "Welche Pflanzen kann ich im Mai sammeln?",
    "Wofür ist Spitzwegerich gut?",
    "Wie verarbeite ich Holunderblüten?",
  ];

  if (!user) {
    return <LoginGate icon={Sparkles} title="KI-Naturcoach" subtitle="Der Coach nutzt Claude Sonnet 4.5. Damit wir das Angebot kostenlos halten können, ist ein kurzer Login nötig." />;
  }

  return (
    <Section>
      <PageHero slug="coach" variant="soft"
        defaultOverline="KI-Coach"
        defaultTitle="Frag den Naturcoach"
        defaultSubtitle="Powered by Claude Sonnet 4.5. Stelle Fragen zu Erkennung, Verwendung und Rezepten."
      />

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 plant-card p-6 flex flex-col h-[70vh] min-h-[500px]" data-testid="coach-chat">
          <div className="flex-1 overflow-y-auto pr-2 space-y-4">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.role}-${i}`}>
                <div className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user" ? "bg-[var(--wp-primary)] text-[var(--wp-cream)] rounded-tr-sm" : "bg-[var(--wp-cream)] rounded-tl-sm"
                }`}>
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="px-4 py-3 rounded-2xl bg-[var(--wp-cream)] text-sm">
                  <span className="inline-block w-2 h-2 rounded-full bg-[var(--wp-primary)] animate-pulse mr-1" />
                  <span className="inline-block w-2 h-2 rounded-full bg-[var(--wp-primary)] animate-pulse mr-1" style={{ animationDelay: "150ms" }} />
                  <span className="inline-block w-2 h-2 rounded-full bg-[var(--wp-primary)] animate-pulse" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>
          <form onSubmit={send} className="mt-4 flex gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Stelle eine Frage…"
              className="flex-1 px-4 py-3 rounded-full border bg-white focus:outline-none focus:border-[var(--wp-primary)]"
              style={{ borderColor: "var(--wp-border)" }}
              data-testid="coach-input"
            />
            <button type="submit" disabled={loading} className="btn-primary" data-testid="coach-send">
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>

        <div>
          <div className="plant-card p-6">
            <div className="overline mb-3 flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" /> Vorschläge</div>
            <div className="space-y-2">
              {suggestions.map((s, i) => (
                <button key={i} onClick={() => setInput(s)} className="w-full text-left text-sm px-3 py-2 rounded-lg hover:bg-[var(--wp-cream)] transition-colors" data-testid={`suggestion-${i}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}
